type ErrorRecord = {
  message?: string;
  statusCode?: number;
  extensions?: { code?: string };
  graphQLErrors?: readonly ErrorRecord[];
  networkError?: ErrorRecord;
  result?: string | { errors?: readonly ErrorRecord[] };
};

export const errorReferenceCodes = {
  schema: 'ARK-API-SCHEMA',
  graphql: 'ARK-API-QUERY',
  http: 'ARK-API-HTTP',
  connection: 'ARK-API-CONNECTION',
  unexpected: 'ARK-PAGE-ERROR',
} as const;

/** Return only classifications: server messages can contain SQL, input values or credentials. */
export function getErrorDiagnostics(error: ErrorRecord | null | undefined) {
  const network = error?.networkError;
  const networkErrors = typeof network?.result === 'object' ? network.result?.errors : undefined;
  const responseErrors = typeof error?.result === 'object' ? error.result?.errors : undefined;
  const errors: ErrorRecord[] = [error?.graphQLErrors, networkErrors, responseErrors]
    .flatMap(items => Array.isArray(items) ? items : [])
    .filter(item => item && typeof item === 'object');
  const schemaMismatch = errors.some(item => item.extensions?.code === 'GRAPHQL_VALIDATION_FAILED'
    || /Cannot query field|Unknown (?:type|argument)|is not defined by type|Variable .* of type .* used in position expecting type/.test(item.message || ''));
  const status = network?.statusCode ?? error?.statusCode;
  const httpStatus = Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined;
  const kind = schemaMismatch ? 'schema' : errors.length ? 'graphql' : httpStatus ? 'http' : network ? 'connection' : 'unexpected';
  return { kind, httpStatus };
}

/** Local administrator display only. Never attach these messages to telemetry or Chat reports. */
export function getAdminErrorMessages(error: ErrorRecord | null | undefined): string[] {
  const nested = [error?.graphQLErrors,
    typeof error?.networkError?.result === 'object' ? error.networkError.result?.errors : undefined,
    typeof error?.result === 'object' ? error.result?.errors : undefined,
  ].flatMap(items => Array.isArray(items) ? items : []);
  const messages = nested.map(item => item?.message);
  if (!messages.length) messages.push(error?.networkError?.message || error?.message);
  return [...new Set(messages.filter((message): message is string => typeof message === 'string' && !!message.trim()))]
    .slice(0, 10).map(message => message.slice(0, 2000));
}

// Metadata is associated with the failing error object, never a global "last query".
const operationMetadata = new WeakMap<object, { operationName: string; eventId?: string }>();
export function rememberErrorOperation(errors: unknown[], operationName: string, eventId?: string) {
  if (!/^[A-Za-z_][A-Za-z0-9_]{0,99}$/.test(operationName)) return;
  for (const error of errors) if (error && typeof error === 'object') {
    operationMetadata.set(error, { operationName, eventId });
  }
}

/** Allowlisted diagnostics only: arbitrary SQL/validation messages can contain submitted values.
 * Keep the backend's sanitizeReportDetail in sync; the server revalidates untrusted input.
 */
export function sanitizeReportDetail(message: string): string | null {
  const schema = message.match(/^(Cannot query field "[A-Za-z_][A-Za-z0-9_]*" on type "[A-Za-z_][A-Za-z0-9_]*"\.|Unknown (?:type|argument) "[A-Za-z_][A-Za-z0-9_]*"\.|Field "[A-Za-z_][A-Za-z0-9_]*" is not defined by type "[A-Za-z_][A-Za-z0-9_]*"\.)/);
  if (schema) return schema[1].slice(0, 500);
  if (/^(NetworkError when attempting to fetch resource\.|Failed to fetch|Load failed|Network request failed)$/.test(message)) return message;
  return null;
}

export function getReportDiagnostics(error: ErrorRecord | null | undefined) {
  const candidates = [error, error?.networkError, ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : [])];
  const metadata = candidates.filter(item => item && typeof item === 'object').map(item => operationMetadata.get(item)).find(Boolean);
  return {
    httpStatus: getErrorDiagnostics(error).httpStatus ?? null,
    operationName: metadata?.operationName ?? null,
    eventId: metadata?.eventId ?? null,
    details: getAdminErrorMessages(error).map(sanitizeReportDetail).filter((message): message is string => message !== null),
  };
}
